<script setup>
import { ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountId } = useAccount();

const pageId = ref('');
const accessToken = ref('');
const inboxName = ref('');
const isCreating = ref(false);

const rules = {
  pageId: { required },
  accessToken: { required },
  inboxName: { required },
};

const v$ = useVuelidate(rules, { pageId, accessToken, inboxName });

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isCreating.value = true;
  try {
    const response = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value.trim(),
      channel: {
        type: 'facebook_token',
        page_id: pageId.value.trim(),
        page_access_token: accessToken.value.trim(),
      },
    });
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: response.id },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.API.ERROR_MESSAGE')
    );
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <form
      class="flex flex-col flex-wrap mx-0 max-w-lg"
      @submit.prevent="createChannel"
    >
      <div class="w-full">
        <PageHeader
          :header-title="t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.TITLE')"
          :header-content="t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.DESC')"
        />
      </div>

      <div class="w-full mt-4">
        <label :class="{ error: v$.inboxName.$error }">
          {{ t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.pageId.$error }">
          {{ t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.PAGE_ID.LABEL') }}
          <input
            v-model="pageId"
            type="text"
            :placeholder="
              t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.PAGE_ID.PLACEHOLDER')
            "
            @blur="v$.pageId.$touch"
          />
          <span v-if="v$.pageId.$error" class="message">
            {{ t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.PAGE_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.accessToken.$error }">
          {{ t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.ACCESS_TOKEN.LABEL') }}
          <textarea
            v-model="accessToken"
            rows="3"
            :placeholder="
              t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.ACCESS_TOKEN.PLACEHOLDER')
            "
            class="font-mono text-sm"
            @blur="v$.accessToken.$touch"
          />
          <span v-if="v$.accessToken.$error" class="message">
            {{ t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.ACCESS_TOKEN.ERROR') }}
          </span>
        </label>
        <p class="text-xs text-n-slate-11 mt-1">
          {{ t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.ACCESS_TOKEN.HELP') }}
        </p>
      </div>

      <div class="w-full text-right mt-4">
        <NextButton
          :label="t('INBOX_MGMT.ADD.FACEBOOK_TOKEN.SUBMIT')"
          :is-loading="isCreating"
        />
      </div>
    </form>
  </div>
</template>
